import { Card, Chip } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ModerationAction } from "@/src/components/ModerationAction";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { useEventActions } from "@/src/pages/EventDetail/hooks/useEventActions";
import type { AdminEvent } from "@/src/pages/Events/events.type";

/**
 * Ce qu'on peut faire d'un événement.
 *
 * ## Trois gestes qui ne disent pas la même chose
 *
 * ⚠️ **Fermer n'est pas cacher.** `close` est l'état *final* d'un événement —
 * la partie est réputée jouée — et l'événement reste parfaitement visible.
 * C'est **désapprouver** qui le soustrait à la vue. Les confondre mentirait à
 * ses inscrits sur ce qui lui est arrivé, et c'est pourquoi l'écran écrit ce
 * que chaque bouton fait plutôt que de le laisser deviner : les deux mots se
 * ressemblent trop.
 *
 * La suppression, elle, est la seule irréversible — et la seule qui emporte le
 * fil de discussion. Elle est donc à gauche, en rouge sans fond, à l'opposé du
 * geste courant.
 */
export function EventActions({ event }: { event: AdminEvent }) {
  const t = useTranslations("moderation");
  const { locale } = useLocale();
  const {
    close,
    isClosing,
    setApproved,
    isSettingApproval,
    remove,
    isRemoving,
  } = useEventActions(event.id);

  const isUnapproved = event.unapprovedAt !== null;
  const isClosed = event.status === "close";
  const isBusy = isClosing || isSettingApproval || isRemoving;

  const since =
    event.unapprovedAt === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date(event.unapprovedAt));

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          {t("actions")}
        </h2>

        <Chip color={isUnapproved ? "danger" : "success"} size="sm">
          {t(isUnapproved ? "event-state-hidden" : "event-state-visible")}
        </Chip>
      </div>

      {isUnapproved ? (
        <div className="flex flex-col gap-1 rounded-lg bg-default p-3 text-default-foreground">
          <p className="text-sm">
            {t("unapproved-since", { date: since ?? "" })}
          </p>

          {event.unapprovedReason ? (
            <p className="text-sm text-pretty whitespace-pre-wrap">
              {event.unapprovedReason}
            </p>
          ) : (
            <p className="text-sm text-muted italic">{t("no-reason")}</p>
          )}

          <div className="flex flex-wrap items-center gap-1 text-xs text-muted">
            <span>{t("decided-by")}</span>
            <AccountBadge account={event.unapprovedBy} isLinked />
          </div>
        </div>
      ) : (
        <p className="text-sm text-pretty text-muted">
          {t("event-visible-note")}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <ModerationAction
          confirmBody={t("delete-event-body", { name: event.name })}
          confirmLabel={t("delete-event-confirm")}
          confirmTitle={t("delete-event-title")}
          isDisabled={isClosing || isSettingApproval}
          isPending={isRemoving}
          label={t("delete-event")}
          pendingLabel={t("deleting")}
          tone="danger"
          onConfirm={() => remove()}
        />

        <span className="mr-auto" />

        {/*
          « Fermer » disparaît une fois l'événement clos : le serveur refuserait
          en 400 `EVENT_ALREADY_IN_STATUS`, et un bouton qui ne peut que
          échouer vaut moins que pas de bouton.
        */}
        {isClosed ? null : (
          <ModerationAction
            confirmBody={t("close-event-body", { name: event.name })}
            confirmLabel={t("close-event-confirm")}
            confirmTitle={t("close-event-title")}
            isDisabled={isBusy && !isClosing}
            isPending={isClosing}
            label={t("close-event")}
            pendingLabel={t("closing")}
            onConfirm={() => close()}
          />
        )}

        {isUnapproved ? (
          <ModerationAction
            confirmBody={t("approve-body", { name: event.name })}
            confirmLabel={t("approve-confirm")}
            confirmTitle={t("approve-title")}
            isDisabled={isBusy && !isSettingApproval}
            isPending={isSettingApproval}
            label={t("approve")}
            pendingLabel={t("approving")}
            onConfirm={() => setApproved(true)}
          />
        ) : (
          <ModerationAction
            confirmBody={t("unapprove-body", { name: event.name })}
            confirmLabel={t("unapprove-confirm")}
            confirmTitle={t("unapprove-title")}
            isDisabled={isBusy && !isSettingApproval}
            isPending={isSettingApproval}
            label={t("unapprove")}
            pendingLabel={t("unapproving")}
            reasonHint={t("reason-hint")}
            reasonLabel={t("reason")}
            tone="danger"
            onConfirm={(reason) => setApproved(false, reason)}
          />
        )}
      </div>
    </Card>
  );
}
